// Wayne AI Platform — Sessions service.
//
// Provides session lifecycle operations. The generated SDK (`sdk.gen.ts`)
// exposes `listSessions` / `getSession` / etc. via `@hey-api/client-fetch`,
// but does NOT yet have a `createSession` endpoint. We bridge that gap here
// using `wayneFetch` (which calls real `/api/sessions` on the backend).
//
// `getOrCreateSession()` is the main entry point for the Play page: it either
// returns the most recent active session or creates a fresh one so the user
// never sees a stale random ID.

import { wayneFetch } from '@/api/wayneFetch';
import { listSessions as listSessionsGenerated } from '@/api/generated/sdk.gen';
import type { SessionSummary } from '@/api/generated/types.gen';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CreateSessionRequest {
  readonly title?: string;
}

export interface CreateSessionResponse {
  readonly sessionId: string;
  readonly createdAt: string;
}

// Re-export for consumers that still need the old shape.
export interface SessionListItem {
  conversationId: string;
  title: string;
  lastMessage: string;
  lastRole: string;
  updatedAt: number;
  messageCount: number;
}

// ---------------------------------------------------------------------------
// createSession — POST /sessions via wayneFetch
// ---------------------------------------------------------------------------

/**
 * Create a new conversation session on the backend.
 *
 * The generated SDK does not include this endpoint yet (pending OpenAPI spec
 * update), so we call it directly via `wayneFetch`.
 */
export async function createSession(
  request?: CreateSessionRequest,
): Promise<CreateSessionResponse> {
  return wayneFetch<CreateSessionResponse>('/sessions', {
    method: 'POST',
    body: { title: request?.title ?? 'New conversation' },
  });
}

// ---------------------------------------------------------------------------
// getOrCreateSession — composite helper
// ---------------------------------------------------------------------------

/**
 * Fetch the most recent active session, or create a new one if none exists.
 *
 * Uses the generated SDK's `listSessions` with `limit=1` to check for an
 * existing active conversation. If one is found, returns it directly.
 * Otherwise, creates a fresh session via `createSession()`.
 *
 * The `listSessionsGenerated` call is wrapped in a try/catch so that any
 * network or server error (4xx, 5xx, timeout) does not propagate — instead
 * we fall through to `createSession()` which is the safe default.
 */
export async function getOrCreateSession(): Promise<CreateSessionResponse> {
  // Try to find the most recent active session via the generated SDK.
  // Wrapped in try/catch: if the list endpoint is unavailable we simply
  // create a new session rather than surfacing an error.
  try {
    const result = await listSessionsGenerated({ query: { limit: 1 } });

    if (result.data) {
      const sessions: readonly SessionSummary[] = result.data.sessions;
      const active = sessions.find((s) => s.status === 'active');
      if (active) {
        return {
          sessionId: active.sessionId,
          createdAt: active.createdAt ?? active.updatedAt,
        };
      }
    }
  } catch {
    // listSessions failed (network error, 4xx, 5xx) — fall through to create.
  }

  // No active session found (or listing failed) — create one.
  return createSession();
}

// ---------------------------------------------------------------------------
// Legacy listSessions (kept for backward compat with Tasks page)
// ---------------------------------------------------------------------------

interface SessionsListResponse {
  sessions: ReadonlyArray<SessionListItem>;
}

/**
 * @deprecated Use `listSessions` from the generated SDK or `getOrCreateSession`.
 * Kept for the Tasks page which still consumes the MSW `/api/sessions` shape.
 */
export async function listSessions(): Promise<ReadonlyArray<SessionListItem>> {
  return (await wayneFetch<SessionsListResponse>('/sessions')).sessions;
}
